import { useState } from 'react';

import { Card } from '@mantine/core';

import { CardSkeletonView } from '../CardSkeleton';
import type { CardShellVariant, CardShellViewProps } from './CardShell.types';

const PADDING_MAP = { sm: 'sm', md: 'md', lg: 'lg' } as const;

const VARIANT_PROPS: Record<CardShellVariant, { withBorder: boolean; bg?: string }> = {
  bordered: { withBorder: true },
  inset: { withBorder: false, bg: 'gray.0' },
  flat: { withBorder: false },
};

export function CardShellView({
  children,
  size = 'md',
  variant = 'bordered',
  loading = false,
  withBorder,
  onClick,
  skeletonRows,
  skeletonAvatar = false,
  h,
  style,
  padding: paddingOverride,
}: CardShellViewProps) {
  const [hovered, setHovered] = useState(false);
  const padding = paddingOverride ?? PADDING_MAP[size];
  const { withBorder: variantWithBorder, bg } = VARIANT_PROPS[variant];
  const isCustomStyle = typeof style === 'function';

  if (loading) {
    return (
      <Card
        withBorder={withBorder ?? variantWithBorder}
        bg={bg}
        radius="md"
        padding={padding}
        h={h}
        style={isCustomStyle ? style({ hovered: false }) : style}
      >
        <CardSkeletonView rows={skeletonRows} avatar={skeletonAvatar} />
      </Card>
    );
  }

  return (
    <Card
      withBorder={withBorder ?? variantWithBorder}
      bg={bg}
      radius="md"
      padding={padding}
      h={h}
      onClick={onClick}
      onMouseEnter={onClick ? () => setHovered(true) : undefined}
      onMouseLeave={onClick ? () => setHovered(false) : undefined}
      style={
        isCustomStyle
          ? style({ hovered })
          : {
              ...style,
              cursor: onClick ? 'pointer' : undefined,
              transition: 'box-shadow 120ms',
              boxShadow: onClick && hovered ? 'var(--mantine-shadow-sm)' : undefined,
            }
      }
    >
      {typeof children === 'function' ? children({ hovered }) : children}
    </Card>
  );
}
